import React, { useEffect, useMemo, useState } from 'react';
import { Clipboard, ExternalLink, FileSpreadsheet, Loader2, Wand2 } from 'lucide-react';
import { apiAIFetch, getUserEmail } from '../api';

const DEFAULT_SAMPLE = `What is 9 x 7?
A) 56
*B) 63
C) 72
D) 49
Explanation: 9 groups of 7 equals 63.

Which organelle is known as the powerhouse of the cell?
1. Nucleus
2. Ribosome
*3. Mitochondria
4. Golgi apparatus
Answer: 3`;

const buildDefaultTitle = (target) => {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, '0');
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
  return `${target === 'gimkit' ? 'Gimkit' : 'Blooket'} Export - ${stamp}`;
};

const normalizeError = async (response) => {
  try {
    const data = await response.json();
    return data || { error: `Request failed (${response.status})` };
  } catch {
    return { error: `Request failed (${response.status})` };
  }
};

const QuestionFormatterPanel = () => {
  const [target, setTarget] = useState('blooket');
  const [rawText, setRawText] = useState('');
  const [sheetTitle, setSheetTitle] = useState(buildDefaultTitle('blooket'));
  const [titleTouched, setTitleTouched] = useState(false);
  const [googleEmail, setGoogleEmail] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [resultData, setResultData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingPreviewMode, setLoadingPreviewMode] = useState('off');
  const [lastPreviewMode, setLastPreviewMode] = useState('off');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');

  useEffect(() => {
    const storedGoogleEmail = localStorage.getItem('lrbrain-google-email');
    setGoogleEmail(storedGoogleEmail || getUserEmail() || '');
  }, []);

  useEffect(() => {
    if (!titleTouched) {
      setSheetTitle(buildDefaultTitle(target));
    }
  }, [target, titleTouched]);

  const parseSummary = useMemo(() => {
    if (resultData?.parse_summary) return resultData.parse_summary;
    if (previewData?.parse_summary) return previewData.parse_summary;
    return null;
  }, [previewData, resultData]);

  const parseErrors = parseSummary?.errors || [];
  const inferenceSummary = previewData?.inference_summary || resultData?.inference_summary || null;
  const inferenceAttempted = (resultData?.inference_attempted ?? previewData?.inference_attempted) || false;
  const inferenceAppliedCount = resultData?.inference_applied_count ?? previewData?.inference_applied_count ?? 0;
  const unresolvedBlockCount = resultData?.unresolved_block_count ?? previewData?.unresolved_block_count ?? 0;
  const activeMode = resultData?.inference_mode_used || previewData?.inference_mode_used || lastPreviewMode || 'off';
  const modeLabel = activeMode === 'missing_answers' ? 'AI-Infer' : 'Strict';
  const inferenceFailed = errorCode === 'gemini_inference_failed' || errorCode === 'gemini_invalid_output';

  const runPreview = async (mode = 'off') => {
    setLoadingPreview(true);
    setLoadingPreviewMode(mode);
    setLastPreviewMode(mode);
    setError('');
    setErrorCode('');
    setResultData(null);
    const action = mode === 'missing_answers' ? 'question_export.preview_infer' : 'question_export.preview';
    try {
      const response = await apiAIFetch('/api/session-planner/question-export/google-sheet', action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target,
          raw_text: rawText,
          preview_only: true,
          inference_mode: mode,
        }),
      });
      const data = await normalizeError(response);
      if (!response.ok) {
        setErrorCode(data.error_code || '');
        const msg = data.error_code === 'gemini_inference_failed' || data.error_code === 'gemini_invalid_output'
          ? 'AI inference failed for unresolved blocks. Fix answer keys manually or retry Infer Missing Answers (AI).'
          : (data.error || 'Failed to parse questions');
        throw new Error(msg);
      }
      setPreviewData(data);
      setErrorCode('');
    } catch (err) {
      setError(err.message || 'Failed to parse questions');
      setPreviewData(null);
    } finally {
      setLoadingPreview(false);
      setLoadingPreviewMode('off');
    }
  };

  const createGoogleSheet = async () => {
    setCreating(true);
    setError('');
    setErrorCode('');
    setResultData(null);
    try {
      const response = await apiAIFetch('/api/session-planner/question-export/google-sheet', 'question_export.create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target,
          raw_text: rawText,
          sheet_title: sheetTitle,
          google_email: googleEmail,
          inference_mode: lastPreviewMode || 'off',
        }),
      });
      const data = await normalizeError(response);
      if (!response.ok) {
        setErrorCode(data.error_code || '');
        const msg = data.error_code === 'google_reconnect_required'
          ? 'Google account needs Sheets permission. Reconnect your Google account in Google Meet tab, then retry.'
          : (data.error_code === 'gemini_inference_failed' || data.error_code === 'gemini_invalid_output')
            ? 'AI inference failed. Run Parse Preview (Strict) or retry Infer Missing Answers (AI).'
          : (data.error || 'Failed to create Google Sheet');
        throw new Error(msg);
      }
      setResultData(data);
      setPreviewData(data);
      setLastPreviewMode(data.inference_mode_used || lastPreviewMode || 'off');
      setErrorCode('');
    } catch (err) {
      setError(err.message || 'Failed to create Google Sheet');
    } finally {
      setCreating(false);
    }
  };

  const handleCopyLink = async () => {
    const url = resultData?.spreadsheet_url;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      window.dispatchEvent(new CustomEvent('app-toast', { detail: 'Sheet link copied.' }));
    } catch {
      window.dispatchEvent(new CustomEvent('app-toast', { detail: 'Could not copy link.' }));
    }
  };

  const headers = previewData?.headers || [];
  const previewRows = previewData?.preview_rows || [];

  return (
    <section className="sp-question-export">
      <div className="sp-question-export__head">
        <h3 className="sp-question-export__title">
          <Wand2 size={16} />
          Question Formatter
        </h3>
        <p className="sp-question-export__subtitle">
          Strict deterministic parsing is the default. Use AI infer only for unresolved missing-answer blocks.
        </p>
      </div>

      <div className="sp-question-export__controls">
        <label className="sp-question-export__field">
          <span>Target Format</span>
          <select value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="blooket">Blooket</option>
            <option value="gimkit">Gimkit</option>
          </select>
        </label>

        <label className="sp-question-export__field sp-question-export__field--wide">
          <span>Google Email</span>
          <input
            type="email"
            value={googleEmail}
            onChange={(e) => setGoogleEmail(e.target.value)}
            placeholder="you@yourdomain.com"
          />
        </label>

        <label className="sp-question-export__field sp-question-export__field--wide">
          <span>Sheet Title</span>
          <input
            type="text"
            value={sheetTitle}
            onChange={(e) => {
              setSheetTitle(e.target.value);
              setTitleTouched(true);
            }}
            placeholder={buildDefaultTitle(target)}
          />
        </label>
      </div>

      <label className="sp-question-export__field sp-question-export__field--full">
        <span>Question Input</span>
        <div className="sp-question-export__hint">
          Accepted examples:
          {' '}<code>1. What is the origin? A. (1,1) B. (1,0) C. (0,0) D. (0,1) Answer: C</code>
          {' '}or multiline
          {' '}<code>Question + A)/B)/C)/D) + Answer:</code>.
        </div>
        <textarea
          className="sp-question-export__textarea"
          value={rawText}
          onChange={(e) => setRawText(e.target.value)}
          placeholder={DEFAULT_SAMPLE}
        />
      </label>

      <div className="sp-question-export__actions">
        <button
          className="sp-btn sp-btn--outline sp-btn--sm"
          onClick={() => runPreview('off')}
          disabled={loadingPreview || !rawText.trim()}
        >
          {loadingPreview && loadingPreviewMode === 'off' ? <Loader2 size={14} className="sp-spin" /> : <Clipboard size={14} />}
          Parse Preview
        </button>
        <button
          className="sp-btn sp-btn--outline sp-btn--sm"
          onClick={() => runPreview('missing_answers')}
          disabled={loadingPreview || !rawText.trim()}
        >
          {loadingPreview && loadingPreviewMode === 'missing_answers' ? <Loader2 size={14} className="sp-spin" /> : <Wand2 size={14} />}
          Infer Missing Answers (AI)
        </button>
        <button
          className="sp-btn sp-btn--primary sp-btn--sm"
          onClick={createGoogleSheet}
          disabled={creating || !rawText.trim()}
        >
          {creating ? <Loader2 size={14} className="sp-spin" /> : <FileSpreadsheet size={14} />}
          Create Google Sheet
        </button>
      </div>

      {error && <div className="sp-question-export__error">{error}</div>}
      {inferenceFailed && activeMode === 'missing_answers' && (
        <div className="sp-question-export__error sp-question-export__error--hard">
          AI inference failed. You can still continue in Strict mode without AI.
        </div>
      )}

      {parseSummary && (
        <div className="sp-question-export__summary">
          <span className={`sp-question-export__mode${activeMode === 'missing_answers' ? ' sp-question-export__mode--infer' : ''}`}>
            Mode: {modeLabel}
          </span>
          <span>Blocks: {parseSummary.total_blocks || 0}</span>
          <span>Parsed: {parseSummary.parsed_count || 0}</span>
          <span>Skipped: {parseSummary.skipped_count || 0}</span>
          <span>Unresolved: {unresolvedBlockCount}</span>
          {inferenceAttempted && (
            <span>Inference applied: {inferenceAppliedCount}</span>
          )}
          {inferenceSummary && (
            <span>Inferred answers: {inferenceSummary.inferred_count || 0}</span>
          )}
        </div>
      )}

      {parseErrors.length > 0 && (
        <div className="sp-question-export__errors">
          {parseErrors.slice(0, 8).map((item, idx) => (
            <div key={`${item.block_index}-${item.line}-${idx}`} className="sp-question-export__error-item">
              {item.error_code === 'missing_answer_key'
                ? `Block ${item.block_index}: Missing answer key. Add Answer:/Correct: or use Infer Missing Answers (AI).`
                : `Block ${item.block_index}, line ${item.line}: ${item.message}`
              }
            </div>
          ))}
          {parseErrors.length > 8 && (
            <div className="sp-question-export__error-item">+{parseErrors.length - 8} more parse issues</div>
          )}
        </div>
      )}

      {headers.length > 0 && previewRows.length > 0 && (
        <div className="sp-question-export__table-wrap">
          <table className="sp-question-export__table">
            <thead>
              <tr>
                {headers.map((h) => <th key={h}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {previewRows.slice(1).map((row, rowIdx) => (
                <tr key={`row-${rowIdx}`}>
                  {headers.map((_, colIdx) => <td key={`r${rowIdx}c${colIdx}`}>{row[colIdx] || ''}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {previewData?.normalized_text && (
        <details className="sp-question-export__normalized">
          <summary>Normalized view ({modeLabel})</summary>
          <pre>{previewData.normalized_text}</pre>
        </details>
      )}

      {resultData?.spreadsheet_url && (
        <div className="sp-question-export__success">
          <span>Sheet created successfully.</span>
          <div className="sp-question-export__success-actions">
            <a href={resultData.spreadsheet_url} target="_blank" rel="noreferrer" className="sp-btn sp-btn--outline sp-btn--sm">
              <ExternalLink size={14} />
              Open Sheet
            </a>
            <button className="sp-btn sp-btn--outline sp-btn--sm" onClick={handleCopyLink}>
              <Clipboard size={14} />
              Copy Link
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default QuestionFormatterPanel;
